// apps/web/src/components/calendar/DateDetailPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import { rruleToHumanReadable } from './utils/rruleHelpers';

/**
 * DateDetailPanel — A-03 (SCRUM-1138), A-05 (SCRUM-1140), A-06 (SCRUM-1141), A-07 (SCRUM-1142)
 * Shows date detail in the right column of the availability page.
 *
 * States:
 *   - Empty: no date selected
 *   - Available: selected date has no booking
 *   - Loading (booked): fetch in flight for booking summary
 *   - Booked: selected date has an active booking
 *   - Error: booking summary fetch failed
 *   - Blocked: selected date is manually blocked
 *   - Repeating block: selected date is blocked by a recurring block pattern
 *
 * B-03 (BlockDateModal) is not yet built — the "Block this date" button
 * is stubbed with a TODO until that story is complete.
 *
 * SCRUM-1140 additions:
 *   - Calls GET /api/v1/availability/:guideId/date/:date/booking-summary on date select
 *   - AbortController discards stale fetches on rapid date switching (race-condition safe)
 *   - PII rule D-03: customer shown as customerFirstName + customerLastInitial only
 *   - "View booking →" navigates to /guide/bookings?bookingId={uuid}
 *   - Booked pill: bg-[#2D5016] text-white
 *
 * SCRUM-1141 additions:
 *   - Blocked state: Blocked pill (#FEE2E2/#B91C1C), reason text, Re-open button
 *   - Null reason falls back to 'No reason recorded'
 *   - prep_day/cleanup_day: view-only, no Re-open button
 *   - block_group_id: shows full range label + TODO B-NEW-06 comment
 *   - Re-open calls toggleDate(available:true) with optimistic update
 *   - API failure: reverts optimistic update + error toast
 *
 * SCRUM-1142 additions:
 *   - Repeating block state: Repeating block pill (#EEF2FF/#3730A3), rrule description, manage link placeholder
 *   - rruleToHumanReadable() converts rrule string to human-readable description
 *   - Fallback to 'Repeating block' if rrule conversion fails or is empty
 *   - 'Manage repeating blocks →' opens RepeatingBlocksList (B-09, SCRUM-1154)
 *   - Informational only — no action buttons
 *
 * @param {string|Date|null} selectedDate - The date selected on the calendar
 * @param {string} guideId - The guide's UUID
 * @param {Object|null} dateInfo - Calendar entry for selectedDate from useGuideAvailability calendarData
 *   Shape: { available, source, blockedReason, block_group_id, blockGroupStart, blockGroupEnd, rrule, recurring_block_id, ... }
 * @param {Function} toggleDate - (dateStr: string, available: boolean) => Promise — from useGuideAvailability
 * @param {Function} onBlockDate - () => void — opens BlockDateModal (B-03)
 * @param {Function} onReopenSuccess - (dateStr: string) => void — called after successful Re-open (A-08)
 * @param {Function} onManageRepeatingBlocks - () => void — opens RepeatingBlocksList (B-09)
 * @param {Function} onEditBlock - (blockGroupId: string) => void — opens BlockDateModal in edit mode (B-NEW-06)
 * @param {Function} onRemoveBlock - (blockGroupId: string) => void — deletes block group (B-NEW-06)
 */
export default function DateDetailPanel({ selectedDate, guideId, dateInfo = null, toggleDate = null, onBlockDate = null, onReopenSuccess = null, onManageRepeatingBlocks = null, onEditBlock = null, onRemoveBlock = null }) {

  // Booked-state fetch state
  const [loading, setLoading] = useState(false);
  const [bookingSummary, setBookingSummary] = useState(null);
  const [fetchError, setFetchError] = useState(false);

  // Blocked state — Re-open flow
  const [reopenError, setReopenError] = useState(null);
  const [isReopening, setIsReopening] = useState(false);
  // Optimistic override: when Re-open is in flight, treat date as available locally
  const [optimisticAvailable, setOptimisticAvailable] = useState(false);

  const navigate = useNavigate();

  const formatDateLabel = (date) => {
    const d = date instanceof Date ? date : toLocalDate(date);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  /**
   * Parse a date string as local midnight, avoiding the UTC-midnight trap where
   * new Date('YYYY-MM-DD') shifts to the previous day in western timezones.
   */
  const toLocalDate = (dateStr) => {
    if (typeof dateStr === 'string') {
      const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(dateStr);
  };

  /**
   * Normalise selectedDate to a YYYY-MM-DD string suitable for the API.
   * The calendar passes a string already; guard against Date objects.
   */
  const toDateParam = (date) => {
    if (!date) {return null;}
    if (typeof date === 'string') {
      // Strip time component if present
      return date.split('T')[0];
    }
    if (date instanceof Date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return null;
  };

  // Determine if the selected date is blocked (and not optimistically re-opened)
  const isBlocked = !optimisticAvailable && dateInfo !== null && dateInfo.available === false;
  const isRepeatingBlock = isBlocked && dateInfo?.source === 'recurring';
  const isManuallyBlocked = isBlocked && dateInfo?.source !== 'recurring';

  /**
   * Fetch booking summary whenever guideId + selectedDate both exist AND the
   * date is NOT blocked (blocked dates skip the booking-summary fetch).
   * AbortController ensures stale requests are discarded on rapid switching.
   */
  useEffect(() => {
    const dateParam = toDateParam(selectedDate);
    if (!guideId || !dateParam) {
      setBookingSummary(null);
      setFetchError(false);
      setLoading(false);
      return;
    }

    // Blocked dates do not need a booking-summary fetch
    if (isBlocked) {
      setBookingSummary(null);
      setFetchError(false);
      setLoading(false);
      return;
    }

    const abortController = new AbortController();

    setLoading(true);
    setBookingSummary(null);
    setFetchError(false);

    apiRequest(
      `/api/v1/availability/${guideId}/date/${dateParam}/booking-summary`,
      { signal: abortController.signal }
    )
      .then((response) => {
        if (!abortController.signal.aborted) {
          setBookingSummary(response.data ?? null);
          setLoading(false);
        }
      })
      .catch((_err) => {
        if (!abortController.signal.aborted) {
          setFetchError(true);
          setLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [guideId, selectedDate, isBlocked]);

  // Reset optimistic state when selectedDate changes
  useEffect(() => {
    setOptimisticAvailable(false);
    setReopenError(null);
    setIsReopening(false);
  }, [selectedDate]);

  const handleRetry = useCallback(() => {
    // Re-trigger the effect by forcing a state flush; simplest approach is to
    // clear error so the parent can re-pass the same selectedDate.
    // We re-run the fetch manually here.
    const dateParam = toDateParam(selectedDate);
    if (!guideId || !dateParam) {return;}

    const abortController = new AbortController();
    setLoading(true);
    setFetchError(false);
    setBookingSummary(null);

    apiRequest(
      `/api/v1/availability/${guideId}/date/${dateParam}/booking-summary`,
      { signal: abortController.signal }
    )
      .then((response) => {
        if (!abortController.signal.aborted) {
          setBookingSummary(response.data ?? null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setFetchError(true);
          setLoading(false);
        }
      });
  }, [guideId, selectedDate]);

  const handleViewBooking = () => {
    navigate(`/guide/bookings?bookingId=${bookingSummary.bookingId}`);
  };

  /**
   * Format a date value (string or Date) to a short human-readable range label.
   * e.g. "May 1" or "May 1 – May 3"
   */
  const formatRangeDate = (dateVal) => {
    if (!dateVal) {return '';}
    const d = dateVal instanceof Date ? dateVal : toLocalDate(dateVal);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  /**
   * Re-open a blocked date: optimistically mark it available, then call toggleDate.
   * Reverts and shows error toast on API failure.
   */
  const handleReopen = useCallback(async () => {
    const dateParam = toDateParam(selectedDate);
    if (!dateParam || !toggleDate) {return;}

    // Optimistic update
    setOptimisticAvailable(true);
    setReopenError(null);
    setIsReopening(true);

    try {
      await toggleDate(dateParam, true);
      // Success: panel transitions to available state (optimisticAvailable remains true)
      if (onReopenSuccess) {
        onReopenSuccess(dateParam);
      }
    } catch (_err) {
      // Revert optimistic update
      setOptimisticAvailable(false);
      setReopenError('Unable to re-open date. Please try again.');
    } finally {
      setIsReopening(false);
    }
  }, [selectedDate, toggleDate, onReopenSuccess]);

  // Derive repeating block rrule description (try/catch; fallback to 'Repeating block')
  let rruleDescription = 'Repeating block';
  if (isRepeatingBlock) {
    try {
      const result = rruleToHumanReadable(dateInfo.rrule ?? '');
      if (result) {
        rruleDescription = result;
      }
    } catch {
      // fallback already set
    }
  }

  // Derive blocked-state display values from dateInfo
  const blockType = dateInfo?.blockedReason ?? null;
  const isBookingAdjacent = blockType === 'prep_day' || blockType === 'cleanup_day';
  const blockGroupId = dateInfo?.block_group_id ?? dateInfo?.blockGroupId ?? null;
  const blockGroupStart = dateInfo?.blockGroupStart ?? null;
  const blockGroupEnd = dateInfo?.blockGroupEnd ?? null;
  const blockGroupLabel = dateInfo?.blockGroupLabel ?? null;
  const isManualBlockGroup = blockGroupId && dateInfo?.groupType === 'manual';

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      {!selectedDate ? (
        // Empty state
        <p style={{ color: '#9CA3AF' }}>
          Select a date to view details or make changes.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Date label: weekday, Month D — no year */}
          <p className="text-gray-900 font-medium">
            {formatDateLabel(selectedDate)}
          </p>

          {/* ── Manually blocked state ── */}
          {isManuallyBlocked && (
            <>
              {/* Blocked status pill */}
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-[#FEE2E2] text-[#B91C1C] w-fit">
                Blocked
              </span>

              {/* Block group range label (B-NEW-06 SCRUM-1153) */}
              {blockGroupId && blockGroupStart && blockGroupEnd && (
                <p className="text-sm text-gray-700" data-testid="block-range-label">
                  {formatRangeDate(blockGroupStart)} – {formatRangeDate(blockGroupEnd)}
                </p>
              )}

              {/* Block group label */}
              {blockGroupId && blockGroupLabel && (
                <p className="text-xs text-gray-500" data-testid="block-group-label">{blockGroupLabel}</p>
              )}

              {/* Block reason text */}
              <p className="text-sm text-gray-600">
                {isBookingAdjacent
                  ? 'Blocked by booking (prep/cleanup day)'
                  : (dateInfo?.blockedReason ?? 'No reason recorded')}
              </p>

              {/* Re-open error message */}
              {reopenError && (
                <p className="text-sm text-red-600">{reopenError}</p>
              )}

              {/* Re-open button — only shown when NOT booking-adjacent and NOT a manual block group */}
              {!isBookingAdjacent && !isManualBlockGroup && (
                <button
                  type="button"
                  onClick={handleReopen}
                  disabled={isReopening}
                  className="mt-2 w-full text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded-lg px-4 py-2 transition-colors"
                >
                  {isReopening ? 'Re-opening…' : 'Re-open this date'}
                </button>
              )}

              {/* B-NEW-06: Edit/Remove buttons for manual block groups (SCRUM-1153) */}
              {isManualBlockGroup && (
                <div className="mt-2 flex flex-col gap-2" data-testid="block-group-actions">
                  <button
                    type="button"
                    onClick={() => onEditBlock?.(blockGroupId)}
                    className="w-full text-sm font-medium text-brand-primary border border-brand-primary rounded-lg py-1.5 hover:bg-brand-primary hover:text-white transition-colors"
                    data-testid="edit-block-button"
                  >
                    Edit block
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveBlock?.(blockGroupId)}
                    className="w-full text-sm text-red-600 border border-red-200 rounded-lg py-1.5 hover:bg-red-50 transition-colors"
                    data-testid="remove-block-button"
                  >
                    Remove block
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── Repeating block state ── */}
          {isRepeatingBlock && (
            <>
              {/* Repeating block status pill */}
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-[#EEF2FF] text-[#3730A3] w-fit">
                Repeating block
              </span>

              {/* rrule human-readable description */}
              <p className="text-sm text-gray-600">{rruleDescription}</p>

              {/* B-09 — link to RepeatingBlocksList (SCRUM-1154) */}
              <button
                onClick={onManageRepeatingBlocks}
                className="text-sm text-brand-primary hover:text-brand-primary-hover underline cursor-pointer bg-transparent border-0 p-0"
                data-testid="manage-repeating-blocks-link"
              >
                Manage repeating blocks →
              </button>
            </>
          )}

          {/* ── Loading skeleton while booking-summary fetch is in flight ── */}
          {!isBlocked && loading && (
            <div data-testid="booking-summary-skeleton" className="flex flex-col gap-2">
              <div className="h-5 w-16 rounded-full bg-gray-200 animate-pulse" />
              <div className="h-16 rounded-lg bg-gray-100 animate-pulse" />
            </div>
          )}

          {/* ── Error state ── */}
          {!isBlocked && !loading && fetchError && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-red-600">Unable to load booking details</p>
              <button
                type="button"
                onClick={handleRetry}
                className="w-full text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 rounded-lg px-4 py-2 transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* ── Booked state ── */}
          {!isBlocked && !loading && !fetchError && bookingSummary && (
            <>
              {/* Booked status pill */}
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-[#2D5016] text-white w-fit">
                Booked
              </span>

              {/* Booking summary card */}
              <div className="rounded-lg border border-gray-200 p-3 flex flex-col gap-1 text-sm text-gray-700">
                <p className="font-medium text-gray-900">
                  {bookingSummary.customerFirstName} {bookingSummary.customerLastInitial}
                </p>
                <p>{bookingSummary.experienceTitle}</p>
                <p className="text-xs text-gray-500">
                  {formatRangeDate(bookingSummary.startDate)}
                  {bookingSummary.endDate &&
                    bookingSummary.endDate !== bookingSummary.startDate
                    ? ` – ${formatRangeDate(bookingSummary.endDate)}`
                    : ''}
                </p>
              </div>

              {/* View booking link */}
              <button
                type="button"
                onClick={handleViewBooking}
                className="mt-1 w-full text-sm font-medium text-white bg-[#2D5016] hover:opacity-90 rounded-lg px-4 py-2 transition-opacity"
              >
                View booking →
              </button>
            </>
          )}

          {/* ── Available state (no booking on this date) ── */}
          {!isBlocked && !loading && !fetchError && !bookingSummary && (
            <>
              {/* Available status pill */}
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 w-fit">
                Available
              </span>

              {/* Block this date — B-03 wired via onBlockDate prop */}
              <button
                type="button"
                onClick={() => onBlockDate?.()}
                className="mt-2 w-full text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 rounded-lg px-4 py-2 transition-colors"
              >
                Block this date
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
